import React from "react";
import Alert from "@components/Alert";
import * as Linking from "expo-linking";
import { useAuthSignOut } from "@utils/firebaseAuth/query/auth";
import { auth } from "@utils/firebase";
import useRevenueCat from "@utils/revenuecat";
import { useData, useEndSession } from "../runtime/provider";
import { captureException } from "@utils/captureException";

const useDeleteAccount = (componentName: string) => {
  const { mutateAsync: logout } = useAuthSignOut(auth);
  const { api, reporting } = useData();
  const endSession = useEndSession();
  const {
    getSubscriptionManagementUrl,
    isPremium: { data: isPremium },
  } = useRevenueCat({ surface: "delete-account" });

  const [isDeleting, setIsDeleting] = React.useState(false);

  const cancel = () => setIsDeleting(false);

  const redirectToCancelSubscription = async () => {
    const cancelLink = await getSubscriptionManagementUrl();

    if (cancelLink) {
      Linking.openURL(cancelLink).catch((error) => {
        captureException(error, {
          channel: "error",
          area: "linking",
          operation: "open-subscription-management",
        });
      });
    } else {
      Alert.alert("Error", "Could not open subscription management page.");
    }
  };

  const checkForActiveSubscription = async () => {
    if (isPremium) {
      Alert.alert(
        "Your subscription is managed separately",
        "Deleting your Lissen account does not cancel your App Store or Google Play subscription. Cancel it first to avoid future charges.",
        [
          {
            text: "Keep account",
            style: "cancel",
            onPress: cancel,
          },
          {
            text: "Manage subscription",
            style: "default",
            onPress: redirectToCancelSubscription,
          },
          {
            text: "Delete without cancelling",
            style: "destructive",
            onPress: firstAlert,
          },
        ],
      );
    } else {
      firstAlert();
    }
  };

  const firstAlert = () =>
    Alert.alert(
      "Are you sure you want to delete your account?",
      "This action cannot be undone.",
      [
        {
          text: "Cancel",
          style: "cancel",
          onPress: cancel,
        },
        {
          text: "Delete",
          style: "destructive",
          onPress: secondAlert,
        },
      ],
    );

  const secondAlert = () =>
    Alert.alert(
      "Are you really sure?",
      "This will permanently delete your Lissen account and data, and log you out immediately.\n\nDeleting your Lissen account does not cancel an App Store or Google Play subscription. You may continue to be charged until you cancel it through the store.\n\nYou can open a new account at any time.",
      [
        {
          text: "Cancel",
          style: "cancel",
          onPress: cancel,
        },
        {
          text: "Delete",
          style: "destructive",
          onPress: deleteAccount,
        },
      ],
    );

  const recentLoginrequiredAlert = () =>
    Alert.alert("You need to login again to delete your account", "", [
      {
        text: "Back to login",
        style: "default",
        onPress: () => {
          logout()
            .finally(() => endSession("sign-out"))
            .catch((error) => {
              captureException(error, {
                channel: "breadcrumb",
                area: "cache",
                operation: "end-session-after-account-delete-relogin",
                tags: { componentName },
              });
            });
        },
      },
    ]);

  const deleteUser = async () => {
    // The account's plays can only be sent while it exists.
    await reporting.flushPlaysBeforeLogout();
    await api.user.delete.mutate();
    reporting.forgetAccount();
    await logout().finally(() => endSession("deleted"));
  };

  const deleteAccount = async () => {
    setIsDeleting(true);
    try {
      await deleteUser();
    } catch (error: unknown) {
      const accountError = error as { code?: string; message?: string };
      if (accountError.code === "auth/requires-recent-login") {
        // Expected re-auth flow, not a failure — handled via UX, not Sentry.
        recentLoginrequiredAlert();
      } else {
        // Surface real deletion failures in Sentry. This path previously only
        // showed an Alert, so LIS-152 reached us by email instead of telemetry.
        captureException(error, {
          channel: "error",
          area: "account",
          operation: "delete-account",
          tags: { componentName },
          extra: { code: accountError.code },
        });
        Alert.alert(
          "Error deleting account",
          accountError.message ?? "Please try again.",
        );
      }
    }
    setIsDeleting(false);
  };

  return {
    deleteAccount: checkForActiveSubscription,
    isDeleting,
  };
};

export default useDeleteAccount;
